import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronRight,
  Plus,
  Star,
  Download,
} from 'lucide-react';
import useDashboardStore from '../../../core/stores/dashboardStore';
import useAuthStore from '../../../core/stores/authStore';
import AppUtil from '../../../core/utils/AppUtil';
import useFavoritesStore from '../../../core/stores/favoritesStore';
import StatusTag from '../../../core/components/dashboard/StatusTag/StatusTag';
import PosPatientModal from '../../../core/components/dashboard/PosPatientModal';
import { getHomeTagState, HIDDEN_STATES_IN_RX } from '../../../core/constants/statusTags';
import { useActionRequiredKpi } from '../../hooks/dashboard/useActionRequiredKpi';
import { useRecommendationHits } from '../../hooks/dashboard/useRecommendationHits';
import { useActiveEvents } from '../../hooks/dashboard/useActiveEvents';
import LogUtil from '../../../core/utils/LogUtil';

/* 상태 태그는 core/constants/statusTags.js의 HOME_STATUS_TO_TAG로 통합
   홈 표는 Figma 15:6425 대로 색 글자, POS·방문중 태그는 표시 안 함 */

// Figma 15:6425 홈 수정본 — 카드 공통 그림자(section-shadow)
const CARD_SHADOW = 'shadow-[0px_0px_22.2px_0px_rgba(67,77,118,0.06)]';

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : iso + 'Z'); // POS 시각은 +09:00 이 붙어 온다
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// last_visit_date: 백엔드가 KST 날짜 'YYYY-MM-DD' 로 줌 → 'MM.DD'
const formatShortDate = (ymd) => (ymd ? ymd.slice(5, 10).replace('-', '.') : '-');

/* ─────────────────────────────────────────
   환자 관리 패널 (실 API: MainLayout 폴링이 채우는 dashboardStore.patientData)
   ───────────────────────────────────────── */

const TABS = [
  { id: 'all',     label: '전체',    key: 'total_patients' },
  { id: 'waiting', label: '승인대기', key: 'waiting_count' },
  { id: 'app',     label: '앱 회원',  key: 'app_members' },
];

// Figma 724:16605 header cells — 라벨 색은 노드값 그대로 (처방전 상태·최근 방문만 #52525b)
const HEADER_COLS = [
  { label: '환자명', color: '#3f3f46' },
  { label: '접수시간', color: '#3f3f46' },
  { label: '회원 상태', color: '#3f3f46' },
  { label: '처방전 상태', color: '#52525b' },
  { label: '최근 방문', color: '#52525b' },
  { label: '휴대폰', color: '#3f3f46' },
  { label: '앱', color: '#3f3f46' },
];
// Figma 724:16606 body row — h44, 셀 px12, border-b #e4e4e7, 가운데 정렬(환자명만 좌측)
const BODY_TD = { height: 44, padding: '0 12px', borderBottom: '1px solid #e4e4e7', verticalAlign: 'middle', textAlign: 'center' };
const BODY_TD_LEFT = { ...BODY_TD, textAlign: 'left' };

const PatientPanel = () => {
  const navigate = useNavigate();
  const patientData = useDashboardStore(s => s.patientData);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(null);

  // 즐겨찾기 환자 store (로그인 시 fetch됨, 단일 진실)
  const favoriteUserIds = useFavoritesStore(s => s.userIds);
  const isPatientFavorite = (p) => {
    const uid = p.user_id ?? p.id;
    return uid != null && favoriteUserIds.includes(uid);
  };

  const patients = patientData?.patients || [];
  const miniKpi = patientData?.mini_kpi || {};

  const filtered = useMemo(() => {
    if (activeTab === 'all') return patients;
    if (activeTab === 'waiting') return patients.filter(p => p.rx_status === 'pending' || p.rx_status === 'received');
    if (activeTab === 'app') return patients.filter(p => p.app_installed);
    return patients;
  }, [patients, activeTab]);

  return (
    <section className="flex flex-col flex-1 min-h-0 gap-4">
      {/* Figma 15:6431 filter_group — 필 탭 (타이틀 제거, 좌상단 이동) */}
      <div className="flex flex-wrap items-start gap-2">
        {TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center justify-center gap-1.5 px-2 py-1 rounded-md text-15 tracking-[-0.3px] whitespace-nowrap transition-colors ${
 active
 ? 'bg-[#e5f2ff] text-[#007aff]'
 : 'bg-zinc-100 text-[#71717a] hover:bg-zinc-200'
 }`}
            >
              <span className={active ? 'font-semibold' : 'font-medium'}>{t.label}</span>
              <span className="font-medium tabular-nums">{miniKpi[t.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Figma 724:16603 table_container — 흰 배경만(카드 border/radius/shadow 없음) */}
      <div className="flex flex-1 min-h-0 bg-white">
        <div className="flex-1 min-w-0 overflow-auto">
          <table
            className="w-full border-separate"
            style={{ tableLayout: 'fixed', borderSpacing: 0 }}
          >
            {/* Figma 724:16605 — 6컬럼 균등(112.67) + 휴대폰 130 */}
            <colgroup>
              <col /><col /><col /><col /><col />
              <col style={{ width: 130 }} />
              <col />
            </colgroup>
            {/* Figma 724:16605 header row — h40, bg #f4f4f5, border-y #e4e4e7, R14, 가운데 정렬(환자명만 좌측) */}
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {HEADER_COLS.map((c, i) => (
                  <th
                    key={c.label}
                    className="text-14 font-normal tracking-[-0.28px] whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{
                      height: 40,
                      padding: '0 12px',
                      textAlign: i === 0 ? 'left' : 'center',
                      verticalAlign: 'middle',
                      color: c.color,
                      background: '#f4f4f5',
                      borderTop: '1px solid #e4e4e7',
                      borderBottom: '1px solid #e4e4e7',
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? null : (
                filtered.map((p, idx) => {
                  // pos_serial/visit_channel/rx_status 종합해서 8개 중 하나로 매핑
                  const tagState = getHomeTagState(p);
                  return (
                    <tr
                      key={`${p.user_id || ''}-${p.order_id || ''}-${idx}`}
                      className="hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedPatient(p)}
                    >
                      {/* 환자명 — M14 #3f3f46 + 별 12 (pt-6, gap 2) */}
                      <td style={BODY_TD_LEFT}>
                        <span className="inline-flex items-start gap-0.5 max-w-full">
                          <span className="text-14 font-medium text-[#3f3f46] tracking-[-0.28px] truncate ">
                            {p.patient_name || '-'}
                          </span>
                          {isPatientFavorite(p) && (
                            <Star size={12} className="fill-amber-400 text-amber-400 shrink-0 mt-1.5" />
                          )}
                        </span>
                      </td>
                      {/* 접수시간 — R14 #71717a */}
                      <td className="text-14 text-[#71717a] tabular-nums tracking-[-0.28px] truncate" style={BODY_TD}>
                        {formatTime(p.visit_time)}
                      </td>
                      {/* 회원 상태 — 앱회원 M14 #3f3f46 / 비회원 R14 #a1a1aa */}
                      <td
                        className={`text-14 tracking-[-0.28px] truncate ${
 p.app_installed ? 'font-medium text-[#3f3f46]' : 'text-[#a1a1aa]'
 }`}
                        style={BODY_TD}
                      >
                        {p.app_installed ? '앱회원' : '비회원'}
                      </td>
                      {/* 처방전 상태 — Figma 15:6425 색 글자. POS·방문중은 시안에 없어 표시 안 함 (방문중은 최근 방문 칸) */}
                      <td style={BODY_TD}>
                        {tagState && !HIDDEN_STATES_IN_RX.includes(tagState)
                          ? <StatusTag state={tagState} variant="text" />
                          : <span className="text-14 text-[#71717a]">-</span>}
                      </td>
                      {/* 최근 방문 — 방문중(M12 #007aff) + 날짜(R14 #71717a), gap 2.
                          is_visiting / last_visit_date(KST) 는 백엔드 필드(명세 v2 A-1) 그대로 */}
                      <td className="text-14 text-[#71717a] tracking-[-0.28px] " style={BODY_TD}>
                        {p.is_visiting && (
                          <span className="block text-12 font-medium text-[#007aff] tracking-normal mb-0.5">방문중</span>
                        )}
                        {formatShortDate(p.last_visit_date)}
                      </td>
                      {/* 휴대폰 — R14 #71717a */}
                      <td className="text-14 text-[#71717a] tabular-nums tracking-[-0.28px] truncate" style={BODY_TD}>
                        {p.phone_number ? AppUtil.formatPhoneNumber(p.phone_number) : '-'}
                      </td>
                      {/* 앱 설치 — R12 #71717a + 아이콘 16, gap 4 */}
                      <td style={BODY_TD}>
                        {!p.app_installed && (
                          <button
                            className="inline-flex items-center gap-1 text-12 text-[#71717a] hover:text-zinc-700 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            앱 설치 <Download size={16} strokeWidth={1.5} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 환자 클릭 시 PosPatientModal 띄움 */}
      {selectedPatient && (
        <PosPatientModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      )}
    </section>
  );
};

/* ─────────────────────────────────────────
   카드 1: 오늘 응대 필요 (Figma 15:6451)
   - 파란 좌측 액센트 보더, 헤더 단순화(전체보기 버튼·구분선 제거)
   - GET /pharm-dashboard/action-required → {title, items[{key,label,count,criteria}]}
   ───────────────────────────────────────── */

const ActionRequiredCard = ({ brn }) => {
  const navigate = useNavigate();
  const { data, error, isLoading } = useActionRequiredKpi(brn);
  useEffect(() => { if (error) LogUtil.error('[Home] action-required-kpi 실패:', error); }, [error]);

  // 응답이 없으면 4f7c1c7(Figma 15:6451) 행 구조 그대로, 숫자만 '-'
  const items = data?.items?.length ? data.items : [
    { key: 'consult', label: '상담 필요 환자', count: null, criteria: '복약이행 ≤ 30%' },
    { key: 'unstable', label: '복약 불안정 환자', count: null, criteria: '최근 미복용 ≥ 2일' },
    { key: 'unanswered', label: '어제 미응답 처방전', count: null, criteria: '접수후 24h+' },
  ];

  return (
    <section className={`flex flex-col gap-3 bg-white border-l-4 border-[#007aff] rounded-lg ${CARD_SHADOW} p-4`}>
      {/* 헤더 — Figma 15:6451 Header (아이콘 + 타이틀, 버튼/구분선 없음) */}
      <header className="flex items-center gap-1">
        <AlertTriangle size={18} className="text-[#007aff] shrink-0" strokeWidth={1.5} />
        <p className="flex-1 text-14 font-semibold text-[#27272a] tracking-[-0.28px] truncate">
          오늘 응대 필요
        </p>
      </header>

      {/* List — Figma 15:6451 List Container, 각 row py-6 */}
      <div className="flex flex-col">
        {isLoading ? null : items.map(item => (
          <div
            key={item.key}
            onClick={() => navigate('/dashboard/adherence')}
            className="flex items-center gap-1.5 py-1.5 cursor-pointer hover:opacity-80"
          >
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <span className="text-12 font-medium text-[#52525b] truncate">
                {item.label}
              </span>
              <span className="text-12 font-medium text-[#52525b] tabular-nums w-5">
                {item.count ?? '-'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-12 text-[#71717a] ">
                {item.criteria}
              </span>
              <ChevronRight size={16} className="text-[#71717a]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   카드 2: 이번 주 추천 적중률 (Figma 15:6452)
   - 헤더 '상세보기', 태그 '+5건 vs 지난주'(아이콘 제거), 2계열 막대(트랙=추천 수, 채움=전환 수)
   - GET /pharm-dashboard/recommendation-hits/{brn}?range=week
     현재 백엔드는 available:false (추천 노출·구매 연결 데이터 없음) → Figma 구조 유지, 값 '-'·빈 차트
     available:true 시 키는 명세 v2 A-2 기준 (total_conversions, delta_vs_previous, period.label,
     daily[{day,recommended,converted}], top_products[{product_id,name,converted_count}])
   ───────────────────────────────────────── */

const CHART_H = 60; // Figma 15:6452 Chart Wrapper h-60

const WeeklyRecommendationCard = ({ brn }) => {
  const { data: hits, error } = useRecommendationHits(brn, 'week');
  useEffect(() => { if (error) LogUtil.error('[Home] recommendation-hits 실패:', error); }, [error]);
  // available!==true 여도 4f7c1c7 카드 구조 그대로 — 값 '-', 차트·TOP3 빈 상태
  const available = hits?.available === true;
  const daily = available ? (hits.daily ?? []) : [];
  const ranking = available ? (hits.top_products ?? []).slice(0, 3) : [];
  const delta = available ? hits.delta_vs_previous : null;
  const max = useMemo(
    () => Math.max(...daily.map(d => d.recommended ?? 0), 1),
    [daily],
  );

  return (
    <section className={`flex flex-col gap-3 bg-white border border-[#f4f4f5] rounded-lg ${CARD_SHADOW} p-4`}>
      {/* 헤더 — Figma 15:6452 (아이콘 없음, 상세보기) */}
      <header className="flex items-center gap-4">
        <p className="flex-1 text-14 font-semibold text-[#27272a] tracking-[-0.28px] truncate">
          이번 주 추천 적중률
        </p>
        <button className="flex items-center gap-1 text-12 text-[#71717a] hover:text-zinc-700 shrink-0">
          상세보기 <ChevronRight size={16} strokeWidth={1.5} />
        </button>
      </header>

      {/* Wrapper: Title + tag + 기간 */}
      <div className="flex flex-col justify-center">
        <div className="flex items-center gap-1">
          <p className="flex-1 text-16 font-semibold text-[#18181b] tracking-[-0.32px] tabular-nums truncate">
            {(available ? hits.total_conversions : null) ?? '-'}건 전환
          </p>
          <span className="inline-flex items-center gap-1 h-[22px] px-1.5 py-0.5 rounded-lg border border-[#e4e4e7] text-(length:--text-12) font-semibold whitespace-nowrap">
            <span className="text-[#007aff]">{delta == null ? '-' : `${delta > 0 ? '+' : ''}${delta}건`}</span>
            <span className="text-[#a1a1aa]">vs</span>
            <span className="text-[#a1a1aa]">지난주</span>
          </span>
        </div>
        <p className="text-12 text-[#71717a] ">
          {(available ? hits.period?.label : null) ?? '-'}
        </p>
      </div>

      {/* Chart container — Figma 15:6452, gap-12 */}
      <div className="flex flex-col gap-3">
        {/* Bar chart — Figma 15:6452: 2계열(트랙=추천 수, 채움=전환 수), 7개 막대, max 대비 h60 스케일 */}
        <div className="flex items-end justify-between" style={{ height: CHART_H }}>
          {daily.map((d, i) => (
            <div key={d.day ?? i} className="relative shrink-0" style={{ width: 30, height: CHART_H }}>
              {/* 트랙 = 추천 수 (text/300 opacity-60, 높이 가변) */}
              <div
                className="absolute inset-x-0 bottom-0 rounded bg-[#d4d4d8]/60"
                style={{ height: `${(d.recommended / max) * CHART_H}px` }}
              />
              {/* 채움 = 전환 수 (cresoty-blue) */}
              <div
                className="absolute inset-x-0 bottom-0 rounded bg-[#1d356d]"
                style={{ height: `${(d.converted / max) * CHART_H}px` }}
              />
            </div>
          ))}
        </div>

        {/* Ranking — Figma 15:6452 data/row, py-6 */}
        <div className="flex flex-col">
          {ranking.map((r, idx) => (
            <div key={r.product_id ?? idx} className="flex items-center gap-1.5 py-1.5">
              <div className="flex flex-1 items-center gap-[9px] min-w-0">
                <span className="text-12 font-medium text-[#52525b] tabular-nums w-2.5">
                  {idx + 1}
                </span>
                <span className="flex-1 text-12 font-medium text-[#52525b] truncate">
                  {r.name ?? '-'}
                </span>
              </div>
              <span className="text-12 text-[#52525b] tabular-nums">
                {r.converted_count ?? '-'}건
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   카드 3: 진행중인 이벤트 (Figma 15:6453)
   - PartyPopper 아이콘 제거, '이벤트 만들기' 버튼 화이트+보더, 진행바 행 border-b
   - GET /pharm-dashboard/active-events → {count, items[{id,type,title,start_date,end_date,used_count,total_count}]}
     쿠폰 이벤트만 (포인트 적립 이벤트는 백엔드 출처 없음 → 표시 안 함)
   ───────────────────────────────────────── */

const ActiveEventsCard = ({ brn }) => {
  const navigate = useNavigate();
  const { data, error } = useActiveEvents(brn);
  useEffect(() => { if (error) LogUtil.error('[Home] active-events 실패:', error); }, [error]);

  const eventsData = (data?.items ?? []).slice(0, 3);

  return (
    <section className={`flex flex-col bg-white border border-[#f4f4f5] rounded-lg ${CARD_SHADOW} overflow-hidden`}>
      {/* 헤더 — Figma 15:6453 data__header (아이콘 제거, pt-16 px-16, 구분선 없음) */}
      <header className="flex items-center gap-4 px-4 pt-4">
        <div className="flex flex-1 items-center gap-1.5 min-w-0">
          <p className="text-14 font-semibold text-[#27272a] tracking-[-0.28px]">
            진행중인 이벤트
          </p>
          <p className="text-14 font-semibold text-[#27272a] tracking-[-0.28px]">
            {data?.count ?? '-'}개
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/coupons/distribute')}
          className="inline-flex items-center gap-1 h-7 px-2 py-1.5 bg-white border border-[#e4e4e7] rounded-md hover:bg-zinc-50 shrink-0"
        >
          <span className="text-14 font-semibold text-[#3f3f46] tracking-[-0.28px] whitespace-nowrap">
            이벤트 만들기
          </span>
          <Plus size={18} className="text-[#3f3f46]" strokeWidth={1.5} />
        </button>
      </header>

      {/* Body — Figma 15:6453 progress_bar 행 (p-16, border-b, gap-8) */}
      <div className="flex flex-col">
        {eventsData.length === 0 ? (
          <p className="text-(length:--text-12) text-zinc-400 text-center py-4">
            진행중인 이벤트가 없습니다
          </p>
        ) : eventsData.map((ev) => {
          const ratio = ev.total_count > 0
            ? Math.min((ev.used_count / ev.total_count) * 100, 100)
            : 0;
          return (
            <div
              key={ev.id}
              className="flex flex-col gap-2 p-4 border-b border-[#e4e4e7] cursor-pointer hover:bg-zinc-50"
            >
              {/* 제목 — Figma: Medium 12 #3f3f46 */}
              <p className="text-12 font-medium text-[#3f3f46] ">
                {ev.title}
              </p>
              {/* Info + Progress */}
              <div className="flex flex-col gap-1 items-end">
                <div className="flex items-center justify-between w-full text-12 text-[#71717a]">
                  <span>{ev.start_date ?? '-'} - {ev.end_date ?? '-'}</span>
                  <span className="text-right whitespace-nowrap">
                    {`${ev.used_count}명 / ${ev.total_count}명 사용`}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#f4f4f5] rounded">
                  <div
                    className="h-2 rounded bg-[#1d356d]"
                    style={{ width: `${ratio}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   HomePage: 좌측 환자 관리 + 우측 KPI 카드 컬럼
   ───────────────────────────────────────── */

const HomePage = () => {
  const pharmacy = useAuthStore(s => s.pharmacy);
  const brn = pharmacy?.business_registration_number;

  return (
    <div className="flex h-full gap-6 p-6 overflow-hidden bg-white">
      <PatientPanel />
      <aside className="flex flex-col gap-3 w-[300px] flex-shrink-0 overflow-y-auto">
        <ActionRequiredCard brn={brn} />
        <WeeklyRecommendationCard brn={brn} />
        <ActiveEventsCard brn={brn} />
      </aside>
    </div>
  );
};

export default HomePage;
